import { reportException } from '@comeon/error-reporter';
import reducerRegistry from '@comeon/reducer-registry';

import { QDType } from '~/components/payment/util/constants';
import { RootState, ThunkAction } from '~/store/types';
import createSliceSelector from '~/utils/create-slice-selector';
import createReducer from '~/utils/createReducer';
import { ErrorReporterDomain } from '../../error-handling/error-reporter-domain';

const API_GET_INSTANT_DEPOSIT_INFO_URL = '/PaymentFormDataService/getInstantDepositInfo';

// Export this so components can reference it
export const REDUCER_NAME = 'quickDeposit';

// Actions
const INITIALIZED = 'QUICK_DEPOSIT/INITIALIZED' as const;
const DISPOSED = 'QUICK_DEPOSIT/DISPOSED' as const;
const FETCH_INSTANT_DEPOSIT_INFO_STARTED = 'QUICK_DEPOSIT/FETCH_INSTANT_DEPOSIT_INFO_STARTED' as const;
const FETCH_INSTANT_DEPOSIT_INFO_COMPLETED = 'QUICK_DEPOSIT/FETCH_INSTANT_DEPOSIT_INFO_COMPLETED' as const;
const FETCH_INSTANT_DEPOSIT_INFO_FAILED = 'QUICK_DEPOSIT/FETCH_INSTANT_DEPOSIT_INFO_FAILED' as const;
const SET_PAYMENT_PIXEL_DATA = 'QUICK_DEPOSIT/SET_PAYMENT_PIXEL_DATA' as const;

// TODO type out these unknown props (or move all of this to an api module?)
export type DepositLimitsAndFeesData = {
	RESULT?: string;
	depositLimitData?: unknown;
	depositFeeAmounts?: unknown;
	bonusAmountsData?: unknown;
	defaultDepositAmounts?: unknown;
	newBalance?: string;
	newBalanceValue?: unknown;
};

export type InstantDepositInfoForMethod = {
	depositMethodId: number;
	paymentMethodName?: string;
	executeDepositUrl?: string;
	paymentMethodNameTextKey?: string;
	depositLimitsAndFeesData?: DepositLimitsAndFeesData;
	depositKycData?: Record<string, unknown>;
	latestUsedDate?: string;
	extraParameters?: Record<string, string>;
};

export type InstantDepositInfo = {
	instantDepositInfoForMethods?: InstantDepositInfoForMethod[];
	lastDepositAmountPlayerCurrency?: string;
	suggestedIncreasedAmount?: string;
	status?: string;
	errorMessage?: string;
	quickDepositAvailable: boolean;
};

export type PaymentPixelData = {
	[key: string]: string;
};

// Types
// Define the state shape for the quick deposit reducer
type State = {
	isInitialized: boolean;
	isFetchingInstantDepositInfo: boolean;
	instantDepositInfo: InstantDepositInfo | null;
	type: QDType;
	paymentPixelData: PaymentPixelData; // Added for pixel tracking data
};

type InitializedAction = {
	type: typeof INITIALIZED;
	payload: { type: QDType };
};

type DisposedAction = {
	type: typeof DISPOSED;
};

type FetchInstantDepositInfoStartedAction = {
	type: typeof FETCH_INSTANT_DEPOSIT_INFO_STARTED;
};

type FetchInstantDepositInfoCompletedAction = {
	type: typeof FETCH_INSTANT_DEPOSIT_INFO_COMPLETED;
	payload: { instantDepositInfo: InstantDepositInfo };
};

type FetchInstantDepositInfoFailedAction = {
	type: typeof FETCH_INSTANT_DEPOSIT_INFO_FAILED;
	payload: { error: unknown };
};

type SetPaymentPixelDataAction = {
	type: typeof SET_PAYMENT_PIXEL_DATA;
	payload: PaymentPixelData;
};

type FetchInstantDepositInfoActions =
	| FetchInstantDepositInfoStartedAction
	| FetchInstantDepositInfoCompletedAction
	| FetchInstantDepositInfoFailedAction;

// Selectors
const selectState = createSliceSelector<State>(REDUCER_NAME);
export const selectIsInitialized = (state: RootState) => selectState(state).isInitialized;
export const selectIsLoading = (state: RootState) => selectState(state).isFetchingInstantDepositInfo;
export const selectInstantDepositInfo = (state: RootState) => selectState(state).instantDepositInfo;
export const selectDepositType = (state: RootState) => selectState(state).type;
export const selectPaymentPixelData = (state: RootState) => selectState(state).paymentPixelData;

const fetchInstantDepositInfoApiCall = async () => {
	const response = await fetch(API_GET_INSTANT_DEPOSIT_INFO_URL);
	return (await response.json()) as InstantDepositInfo;
};

const fetchInstantDepositInfo = (): ThunkAction<FetchInstantDepositInfoActions> => async dispatch => {
	try {
		dispatch({ type: FETCH_INSTANT_DEPOSIT_INFO_STARTED });
		const instantDepositInfo = await fetchInstantDepositInfoApiCall();
		dispatch({
			type: FETCH_INSTANT_DEPOSIT_INFO_COMPLETED,
			payload: { instantDepositInfo: { ...instantDepositInfo } }
		});
	} catch (error) {
		reportException(new Error('Could not fetch instant deposit info for quick deposit', { cause: error }), {
			domain: ErrorReporterDomain.PAYMENT
		});

		dispatch({ type: FETCH_INSTANT_DEPOSIT_INFO_FAILED, payload: { error } });
	}
};

export const initialize =
	(shouldRefreshOnInit: boolean, type: QDType): ThunkAction<InitializedAction> =>
	(dispatch, getState) => {
		if (!selectIsInitialized(getState()) || shouldRefreshOnInit) {
			dispatch(fetchInstantDepositInfo());
		}

		dispatch({ type: INITIALIZED, payload: { type } });
	};

export const setPaymentPixelData =
	(pixelData: PaymentPixelData): ThunkAction<SetPaymentPixelDataAction> =>
	dispatch => {
		dispatch({ type: SET_PAYMENT_PIXEL_DATA, payload: pixelData });
	};

export const dispose = (): DisposedAction => ({ type: DISPOSED });

// Reducer
const initialState: State = {
	isInitialized: false,
	isFetchingInstantDepositInfo: false,
	instantDepositInfo: null,
	type: QDType.casino,
	paymentPixelData: {}
};

const handleInitialized = (state: State, { payload }: InitializedAction) => {
	return {
		...state,
		isInitialized: true,
		type: payload.type
	};
};

const handleFetchInstantDepositInfoStarted = (state: State) => {
	if (!state.isInitialized) {
		return state;
	}

	return {
		...state,
		isFetchingInstantDepositInfo: true
	};
};

const handleFetchInstantDepositInfoCompleted = (state: State, { payload }: FetchInstantDepositInfoCompletedAction) => {
	if (!state.isInitialized) {
		return state;
	}

	return {
		...state,
		isFetchingInstantDepositInfo: false,
		instantDepositInfo: payload.instantDepositInfo
	};
};

const handleFetchInstantDepositInfoFailed = (state: State) => {
	if (!state.isInitialized) {
		return state;
	}

	return {
		...state,
		isFetchingInstantDepositInfo: false
	};
};

const handleDisposed = () => initialState;

const handleSetPaymentPixelData = (state: State, { payload }: SetPaymentPixelDataAction) => {
	return {
		...state,
		paymentPixelData: payload
	};
};

const reducer = createReducer(initialState, {
	[INITIALIZED]: handleInitialized,
	[DISPOSED]: handleDisposed,
	[FETCH_INSTANT_DEPOSIT_INFO_STARTED]: handleFetchInstantDepositInfoStarted,
	[FETCH_INSTANT_DEPOSIT_INFO_COMPLETED]: handleFetchInstantDepositInfoCompleted,
	[FETCH_INSTANT_DEPOSIT_INFO_FAILED]: handleFetchInstantDepositInfoFailed,
	[SET_PAYMENT_PIXEL_DATA]: handleSetPaymentPixelData
});

reducerRegistry.register(REDUCER_NAME, reducer);

export default reducer;
